import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import Spinner from '@/components/Spinner';

import { DEFAULT_CURRENCY, STATUS } from '../constants';
import PaymentError from './PaymentError';
import useAdyenCheckout from './hooks/useAdyenCheckout';
import './styles.css';

function PaymentElement({
	priceId,
	mainPriceId = null,
	mode = 'checkout',
	showPayButton = true,
	customPayButton = false,
	termsNode = null,
	currency,
	onSuccess: onSuccessProp,
	onError: onErrorProp,
	buttonProps = {},
	validateFn = null,
	checkoutMessage: checkoutMessageProp,
	updateMessage: updateMessageProp,
	onStatusChange = () => {},
	onPaymentFormReady = () => {},
	onlyAttachAndSwitch = false
}) {
	const isUpdateMode = mode === 'update';
	const isCheckoutMode = mode === 'checkout';

	const [loading, setLoading] = useState(false);

	const { t, i18n } = useTranslation();

	const onSuccess = result => {
		setLoading(false);

		onSuccessProp?.(result);
	};

	const onError = (...params) => {
		setLoading(false);
		onErrorProp?.(...params);
	};

	const checkoutMessage = checkoutMessageProp || t('checkout.finishPurchase');

	const updateMessage = updateMessageProp || t('common.accept');

	const schemaName = t('common.card');

	const submitButtonLabel = isUpdateMode ? updateMessage : checkoutMessage;

	const {
		containerRef,
		componentRef,
		isReady,
		isPaymentFormReady,
		paymentError,
		isActionMode,
		status,
		isResolving3ds
	} = useAdyenCheckout({
		priceId,
		mainPriceId,
		currency: currency || DEFAULT_CURRENCY,
		attachPaymentMethod: isUpdateMode,
		onSuccess,
		onError,
		showPayButton: showPayButton && !customPayButton,
		locale: i18n.language,
		translations: {
			payAmountFormat: submitButtonLabel,
			'payButton.saveDetails': submitButtonLabel
		},
		paymentMethodNamesMapping: {
			scheme: schemaName
		},
		onlyAttachAndSwitch
	});

	function handlePay() {
		if (loading) return;

		setLoading(true);

		if (validateFn && !validateFn() && isCheckoutMode && customPayButton) {
			return setLoading(false);
		}

		componentRef.current?.submit();
	}

	useEffect(() => {
		onStatusChange?.(status);
	}, [status]);

	useEffect(() => {
		onPaymentFormReady?.(isPaymentFormReady);
	}, [isPaymentFormReady]);

	return (
		<div className="custom-adyen-checkout">
			<div className="w-full">
				{(!isReady || isResolving3ds) && (
					<div className="flex min-h-[300px] w-full items-center justify-center">
						<Spinner className="mx-auto mt-10" size={50} />
					</div>
				)}

				<div ref={containerRef} />

				{paymentError && (
					<PaymentError>
						{t(`checkout.adyen.paymentErrors.${paymentError}`)}
					</PaymentError>
				)}

				{termsNode &&
					isReady &&
					isCheckoutMode &&
					!isActionMode &&
					[STATUS.INITIAL, STATUS.PROCESSING].includes(status) &&
					termsNode}

				{showPayButton && customPayButton && isReady && (
					<Button
						variant="contained"
						color="primary"
						size="md"
						className="mt-4"
						{...buttonProps}
						loading={loading}
						type="button"
						onClick={handlePay}
					>
						{submitButtonLabel}
					</Button>
				)}
			</div>
		</div>
	);
}

export default PaymentElement;
